import type { ComponentProps } from "react";
import type { ComposedTextScoreCard } from "@ptsd/uikit";

import type { RouterOutputs } from "~/utils/api";
import { isPositiveNum } from "~/utils/is-positive-num";

type ScoreState = ComponentProps<typeof ComposedTextScoreCard.Score>["state"];
type DynamicsState = ComponentProps<
  typeof ComposedTextScoreCard.Dynamics
>["state"];

interface ScoreCardProps {
  score: number | null | undefined;
  dynamicsState: DynamicsState;
  description: string | null | undefined;
  dynamics: string | null | undefined;
  scoreState: ScoreState;
}

export interface ComposedTextProps {
  title?: string | null | undefined;
  markdown?: string | null | undefined;
  scoreCard?: ScoreCardProps;
}

type SpheresScaleData = Omit<
  RouterOutputs["dashboard"]["getSpheresScale"],
  "data"
>;

type SphereLevel =
  RouterOutputs["dashboard"]["getSpheresScale"]["hmt"]["level"]["level"];

export const constructDynamicsText = (dynamic: number | null) => {
  if (dynamic === null) {
    return "0 балів від останньої діагностики";
  }
  if (isPositiveNum(dynamic)) {
    return `+${dynamic} балів від останньої діагностики`;
  }
  if (!isPositiveNum(dynamic)) {
    return `${dynamic} балів від останньої діагностики`;
  }
};

export const getDynamicsStatus = (dynamics: unknown) => {
  const isNumber = typeof dynamics === "number" && !isNaN(dynamics);
  if (!isNumber) return "neutral";
  if (dynamics === 0) return "neutral";
  if (dynamics > 0) return "success";
  if (dynamics < 0) return "danger";
};

const sphereLevelToscoreState = {
  CRITICAL: "danger",
  MIDDLE: "warning",
  HIGH: "success",
  RESOURCE: "success",
} satisfies Record<SphereLevel, ScoreState>;

export const normalizeComposedTextData = (data: SpheresScaleData) => {
  return {
    title: data.hmt.title,
    markdown: data.text,
    scoreCard: {
      score: data.hmt.score,
      dynamicsState: getDynamicsStatus(data.dynamic),
      description: data.hmt.level.name,
      dynamics: constructDynamicsText(data.dynamic),
      scoreState: sphereLevelToscoreState[data.hmt.level.level],
    },
  } satisfies ComposedTextProps;
};

type SphereInfo = RouterOutputs["dashboard"]["getSphereInfo"];
type SphereInfoComposedTextProps = Omit<
  SphereInfo,
  "recommendations" | "scales"
>;

interface AccordionData {
  id: string;
  title: string;
  content: string;
}

export interface SphereComposedText {
  description: {
    md: string;
    accordion: AccordionData[];
  };
  title: string;
  image: string;
  score: string | number;
  state: (typeof sphereLevelToscoreState)[keyof typeof sphereLevelToscoreState];
  dynamics: {
    //   state: string;
    description: string;
    //    text: string;
  };
}

export const normalizeSphereComposedText = (
  data: SphereInfoComposedTextProps,
) => {
  return {
    description: {
      md: data.interpretation,
      accordion: [
        {
          id: "sphere-description",
          title: data.description.title,
          content: data.description.text,
        },
      ],
    },
    title: data.title,
    image: "/recommendations/default.png",
    score: data.score,
    state: sphereLevelToscoreState[data.level.level],
    dynamics: {
      description: data.level.name,
    },
  } satisfies SphereComposedText;
};
